import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './hooks';
import { Login } from './components/auth/Login';
import { AuthProvider } from './contexts/AuthContext';
import { Dashboard } from './components/dashboard/Dashboard';
import { Navigation } from './components/common/Navigation';
import ErrorBoundary from './components/common/ErrorBoundary';
import { ProjectsList } from './components/projects/ProjectsList/ProjectsList';
import { ClientsList } from './components/clients/ClientsList/ClientsList';
import ProjectDetailWrapper from './components/projects/ProjectDetail/ProjectDetailWrapper';
import ClientDetailWrapper from './components/clients/ClientDetail/ClientDetailWrapper';
import { TeamWorkload } from './components/team/TeamWorkload';
import { Administrator } from './components/admin/Administrator';

// Protected Route component that checks navVisibility
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredNav: 'dashboard' | 'clients' | 'projects' | 'team' | 'admin';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredNav }) => {
  const { user } = useAuth();

  // Get nav visibility from user, with defaults
  const navVisibility = user?.navVisibility || {
    dashboard: true,
    clients: true,
    projects: true,
    team: true,
    admin: user?.isAdmin || false
  };

  // Check if user has access to this route
  if (!navVisibility[requiredNav]) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Main App Component
const App: React.FC = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
};

const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50">
        <NavigationWrapper />
        <div className="lg:pl-28">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={
              <ProtectedRoute requiredNav="dashboard">
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/clients" element={
              <ProtectedRoute requiredNav="clients">
                <ClientsList />
              </ProtectedRoute>
            } />
            <Route path="/clients/:id" element={
              <ProtectedRoute requiredNav="clients">
                <ClientDetailWrapper />
              </ProtectedRoute>
            } />
            <Route path="/projects" element={
              <ProtectedRoute requiredNav="projects">
                <ProjectsList />
              </ProtectedRoute>
            } />
            <Route path="/projects/:id" element={
              <ProtectedRoute requiredNav="projects">
                <ProjectDetailWrapper />
              </ProtectedRoute>
            } />
            <Route path="/team" element={
              <ProtectedRoute requiredNav="team">
                <TeamWorkload />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute requiredNav="admin">
                <Administrator />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </div>
    </ErrorBoundary>
  );
};

// Navigation wrapper to get current route
const NavigationWrapper: React.FC = () => {
  const navigate = useNavigate();
  const location = window.location.pathname;

  const getActiveView = () => {
    if (location.startsWith('/clients')) return 'clients';
    if (location.startsWith('/projects')) return 'projects';
    if (location.startsWith('/team')) return 'team';
    if (location.startsWith('/admin')) return 'admin';
    if (location.startsWith('/dashboard')) return 'dashboard';
    return 'dashboard';
  };

  const handleNavigate = (view: string) => {
    navigate(`/${view}`);
  };

  return <Navigation activeView={getActiveView()} onNavigate={handleNavigate} />;
};

export default App;